export interface PlaidLinkConfig {
  clientName: string;
  env: 'sandbox' | 'development' | 'production';
  product: string[];
  countryCodes: string[];
  language: string;
}

export interface PlaidTransaction {
  transaction_id: string;
  account_id: string;
  amount: number;
  date: string;
  name: string;
  merchant_name?: string;
  category?: string[];
  pending: boolean;
  payment_channel: string;
}

export interface PlaidAccount {
  account_id: string;
  name: string;
  official_name?: string;
  type: string;
  subtype: string;
  mask?: string;
}

// Fetch link token from backend
export async function createLinkToken(): Promise<string> {
  try {
    const response = await fetch('/.netlify/functions/plaid-link-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to create link token');
    }

    const data = await response.json();
    return data.link_token;
  } catch (error) {
    throw new Error('Unable to initialize Plaid. Please check configuration.');
  }
}

// Exchange public token for access token
export async function exchangePublicToken(publicToken: string): Promise<string> {
  try {
    const response = await fetch('/.netlify/functions/plaid-exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ public_token: publicToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange token');
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    throw new Error('Failed to exchange token. Please try again.');
  }
}

// Generate additional sample transactions for better visualization in sandbox
function generateSampleTransactions(existingTransactions: PlaidTransaction[]): PlaidTransaction[] {
  const sampleExpenses = [
    { name: 'Office Depot', amount: 127.50, category: ['Shops', 'Office Supplies'] },
    { name: 'Starbucks', amount: 15.80, category: ['Food and Drink', 'Coffee Shop'] },
    { name: 'Shell Gas Station', amount: 52.00, category: ['Travel', 'Gas Stations'] },
    { name: 'Amazon Business', amount: 243.99, category: ['Shops', 'Office Supplies'] },
    { name: 'UPS Store', amount: 35.20, category: ['Service', 'Shipping'] },
    { name: 'Zoom.us', amount: 14.99, category: ['Service', 'Software'] },
    { name: 'Adobe Creative Cloud', amount: 54.99, category: ['Service', 'Software'] },
    { name: 'Costco', amount: 189.45, category: ['Shops', 'Wholesale'] },
    { name: 'AT&T', amount: 95.00, category: ['Service', 'Phone'] },
    { name: 'Hilton Hotels', amount: 189.00, category: ['Travel', 'Hotels'] },
    { name: 'Delta Airlines', amount: 425.00, category: ['Travel', 'Airlines'] },
    { name: 'LinkedIn Premium', amount: 39.99, category: ['Service', 'Professional Services'] },
    { name: 'FedEx Office', amount: 67.30, category: ['Service', 'Shipping'] },
    { name: 'Best Buy', amount: 299.99, category: ['Shops', 'Electronics'] },
    { name: 'Panera Bread', amount: 24.50, category: ['Food and Drink', 'Restaurants'] },
  ];

  const additionalTransactions: PlaidTransaction[] = [];
  const today = new Date();
  
  // Generate transactions for the past 3 months
  for (let i = 0; i < 30; i++) {
    const daysAgo = Math.floor(Math.random() * 90);
    const date = new Date(today);
    date.setDate(date.getDate() - daysAgo);
    
    const expense = sampleExpenses[i % sampleExpenses.length];
    const randomVariation = 0.8 + Math.random() * 0.4; // ±20% variation
    
    additionalTransactions.push({
      transaction_id: `sample_${Date.now()}_${i}`,
      account_id: 'sample_account',
      amount: parseFloat((expense.amount * randomVariation).toFixed(2)),
      date: date.toISOString().split('T')[0],
      name: expense.name,
      merchant_name: expense.name,
      category: expense.category,
      pending: false,
      payment_channel: 'in store',
    });
  }

  return [...existingTransactions, ...additionalTransactions];
}

// Fetch transactions from Plaid
export async function fetchTransactions(
  accessToken: string,
  startDate: string,
  endDate: string
): Promise<{ transactions: PlaidTransaction[]; accounts: PlaidAccount[] }> {
  try {
    const response = await fetch('/.netlify/functions/plaid-transactions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        access_token: accessToken,
        start_date: startDate,
        end_date: endDate,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch transactions');
    }

    const data = await response.json();
    
    // In sandbox mode, enhance with additional sample data for better charts
    if (accessToken.includes('sandbox') || window.location.hostname === 'localhost') {
      data.transactions = generateSampleTransactions(data.transactions);
    }
    
    return data;
  } catch (error) {
    throw new Error('Failed to fetch transactions. Please try again or contact support.');
  }
}

// Store access token (encrypted in production!) - user-specific
export function storePlaidAccessToken(accessToken: string, userId?: string | null): void {
  // WARNING: In production, this should be stored on the backend with encryption
  // For now, storing in localStorage for development
  if (!userId) {
    const authData = localStorage.getItem('expense_tracker_auth');
    if (!authData) return;
    const auth = JSON.parse(authData);
    userId = auth.currentUser?.email;
  }
  const key = `plaid_access_token_${userId}`;
  localStorage.setItem(key, accessToken);
}

// Get stored Plaid access token (user-specific)
export function getPlaidAccessToken(userId?: string | null): string | null {
  if (!userId) {
    const authData = localStorage.getItem('expense_tracker_auth');
    if (!authData) return null;
    const auth = JSON.parse(authData);
    userId = auth.currentUser?.email;
  }
  const key = `plaid_access_token_${userId}`;
  return localStorage.getItem(key);
}

// Check if bank is connected (user-specific)
export function isBankConnected(userId?: string | null): boolean {
  if (!userId) {
    const authData = localStorage.getItem('expense_tracker_auth');
    if (!authData) return false;
    const auth = JSON.parse(authData);
    userId = auth.currentUser?.email;
  }
  const key = `plaid_access_token_${userId}`;
  return localStorage.getItem(key) !== null;
}

// Disconnect bank (user-specific)
export function disconnectBank(userId?: string | null): void {
  if (!userId) {
    const authData = localStorage.getItem('expense_tracker_auth');
    if (!authData) return;
    const auth = JSON.parse(authData);
    userId = auth.currentUser?.email;
  }
  const tokenKey = `plaid_access_token_${userId}`;
  const accountsKey = `plaid_accounts_${userId}`;
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(accountsKey);
}
